import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';

export default class extends Controller {
  static values = {
    options: Array,
    disableSearch: Boolean,
  };

  connect () {
    if (!this.element.tomselect) {
      (() => new TomSelect(this.element, this._settingsFactory(this.element)))();
    }

    // Sync the tom-select widget when a password manager or the browser
    // autofills the underlying <select> directly: those actors don't know
    // about tom-select and only touch the native control.
    this._syncFromNative = this._syncFromNative.bind(this);
    this.element.addEventListener('change', this._syncFromNative);
    this._nativeObserver = new MutationObserver(this._syncFromNative);
    this._nativeObserver.observe(this.element, {
      subtree: true,
      attributes: true,
      attributeFilter: ['selected'],
    });
  }

  disconnect () {
    const value = this.element.value;

    this._nativeObserver?.disconnect();
    this.element.removeEventListener('change', this._syncFromNative);
    this.element.tomselect.destroy();

    // necessary to persist value because
    // this.element.tomselect.destroy() sets the value to null
    this.element.value = value;
  }

  _syncFromNative (event) {
    if (event && event.type === 'change' && !event.isTrusted) return;

    const nativeValue = this.element.value;
    const widget = this.element.tomselect;
    if (!widget || !nativeValue || nativeValue === widget.getValue()) return;

    widget.setValue(nativeValue, true);
  }

  _settingsFactory (element) {
    const ajaxUrl = element.dataset.ajaxUrl;
    const ajaxTextField = element.dataset.ajaxTextField;
    const ajaxValueField = element.dataset.ajaxValueField;
    const ajaxMatcher = element.dataset.ajaxSearchMatcher;
    const ajaxDisplayedField = (
      element.dataset.ajaxDisplayedField ? element.dataset.ajaxDisplayedField : ajaxTextField
    );
    const ajaxAdditionalFilters = (
      element.dataset.ajaxAdditionalFilters
        ? JSON.parse(element.dataset.ajaxAdditionalFilters)
        : []
    );

    const additionalFiltersQuery = ajaxAdditionalFilters.reduce((acc, filter) => {
      return acc + `&q[${filter[0]}]=${filter[1]}`;
    }, '');

    const formatAjaxData = (json, valueField, textField) => {
      return json.map(entry => (
        {
          value: entry[valueField],
          text: entry[textField]
        }
      ));
    };

    const settingsStore = {
      create: {
        create: input => ({ value: '', text: input }),
        persist: false
      },
      ajax: {
        valueField: 'value',
        labelField: 'text',
        searchField: 'text',
        load: function (query, callback) {
          const queryParam = `q[${ajaxTextField}_${ajaxMatcher}]=${encodeURIComponent(query)}`;
          const url = `${ajaxUrl}?${queryParam}${additionalFiltersQuery}`;
          fetch(url, {
            headers: {
              Accept: 'application/json'
            }
          })
            .then(response => response.json())
            .then(json => {
              callback(formatAjaxData(json, ajaxValueField, ajaxDisplayedField));
            }).catch((e) => {
              callback();
            });
        }
      }
    };

    const settings = {
      placeholder: element.dataset.placeholder || '',
      maxOptions: null,
      render: {
        no_results: () => '<div class="no-results">Aucun résultat trouvé</div>',
      },
      onItemAdd: function () {
        this.setTextboxValue('');
      },
      plugins: {
        remove_button: {
          title: 'remove'
        }
      },
    };

    if (this.disableSearchValue) {
      settings.controlInput = null;
      delete settings.plugins.remove_button;
    }

    if (this.hasOptionsValue) { Object.assign(settings, settings, {options: this.optionsValue}); }
    if (element.classList.contains('tom-select-ajax')) { Object.assign(settings, settings, settingsStore.ajax); }
    if (element.classList.contains('tom-select-create')) { Object.assign(settings, settings, settingsStore.create); }

    return settings;
  }
}
